import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { QueryState } from '@/shared/ui/QueryState'
import { api } from '@/shared/lib/api'
import { makeLabeler, makeRegionLabeler, useCommonCodes, useRegions, useTargetCodes } from '@/shared/lib/codes'
import { SolutionDetailMobile } from '@/public/features/solution/ui/SolutionDetailMobile'
import { SolutionDetailPc } from '@/public/features/solution/ui/SolutionDetailPc'

export function SolutionDetailPage() {
  const { id } = useParams<{ id: string }>()
  const card = useQuery({
    queryKey: ['solution-card', id],
    queryFn: () => api.solutionCard(id ?? ''),
    enabled: Boolean(id),
  })

  const categoryLabel = makeLabeler(useCommonCodes('category').data)
  const targetLabel = makeLabeler(useTargetCodes().data)
  const helpTypeLabel = makeLabeler(useCommonCodes('help_type').data)
  const resourceLabel = makeLabeler(useCommonCodes('resource').data)
  const areaLabel = makeLabeler(useCommonCodes('service_area').data)
  const costLabel = makeLabeler(useCommonCodes('cost_type').data)
  const pilotLabel = makeLabeler(useCommonCodes('pilot_available').data)
  const regionLabel = makeRegionLabeler(useRegions().data)

  const c = card.data

  return (
    <>
      <SolutionDetailMobile query={card} />
      <SolutionDetailPc id={id} query={card} />

      <div className="desktop-screen">
      <Link className="mb-4 inline-block text-label text-black-100" to="/solutions">
        ← 기업해결카드 목록
      </Link>
      <QueryState isPending={card.isPending} error={card.error}>
        {c ? (
          <article className="rounded-[10px] border border-white-300 bg-white-50 p-7">
            <div className="mb-5 border-b border-white-300 pb-5">
              <div className="mb-2 text-caption text-white-900">
                <Link to={`/orgs/${c.org.id}`}>{c.org.name}</Link>
              </div>
              <h1 className="m-0 mb-1.5 text-headline-sm font-bold tracking-[-0.02em]">{c.title}</h1>
              <p className="text-black-100" style={{ margin: 0 }}>
                {c.summary}
              </p>
            </div>

            <h2 className="mt-10 mb-3.5 text-body-md font-bold" style={{ marginTop: 0 }}>
              이런 문제를 해결합니다
            </h2>
            <p style={{ whiteSpace: 'pre-wrap' }}>{c.problemSolved}</p>

            <h2 className="mt-10 mb-3.5 text-body-md font-bold">서비스 내용</h2>
            <p style={{ whiteSpace: 'pre-wrap' }}>{c.body}</p>

            <h2 className="mt-10 mb-3.5 text-body-md font-bold">조건</h2>
            <dl className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-2.5 text-body-sm">
              <dt>분야</dt>
              <dd>{c.categoryCodes.map(categoryLabel).join(' · ')}</dd>

              <dt>대상</dt>
              <dd>{c.targetCodes.map(targetLabel).join(' · ')}</dd>

              <dt>도움방식</dt>
              <dd>{c.helpTypeCodes.map(helpTypeLabel).join(' · ')}</dd>

              <dt>제공 자원</dt>
              <dd>{c.resourceCodes.map(resourceLabel).join(' · ')}</dd>

              <dt>서비스 지역</dt>
              <dd>
                {c.serviceAreaCodes.map(areaLabel).join(' · ')}
                {c.serviceDongCodes && c.serviceDongCodes.length > 0
                  ? ` (${c.serviceDongCodes.map(regionLabel).join(' · ')})`
                  : ''}
              </dd>

              <dt>비용</dt>
              <dd>{costLabel(c.costType)}</dd>

              <dt>이용 조건</dt>
              <dd>{c.accessCondition ?? <span className="text-black-100">확인 필요</span>}</dd>

              <dt>실증 가능 여부</dt>
              <dd>{pilotLabel(c.pilotAvailable)}</dd>

              <dt>수용 규모</dt>
              <dd>{c.capacityNote ?? <span className="text-black-100">확인 필요</span>}</dd>

              <dt>착수까지</dt>
              <dd>
                {c.leadTimeDays !== null ? `약 ${c.leadTimeDays}일` : <span className="text-black-100">확인 필요</span>}
              </dd>

              <dt>성과</dt>
              <dd>{c.impactNote ?? <span className="text-black-100">확인 필요</span>}</dd>
            </dl>

            {c.tags && c.tags.length > 0 ? (
              <div className="flex flex-wrap gap-[5px]" style={{ marginTop: 20 }}>
                {c.tags.map((t) => (
                  <span key={t} className="rounded-full bg-white-200 px-[9px] py-[3px] text-[11px] text-black-500">
                    #{t}
                  </span>
                ))}
              </div>
            ) : null}
          </article>
        ) : null}
      </QueryState>
      </div>
    </>
  )
}
